/** Остаток склада-источника под строкой потребности (проверка перемещений).
 *
 * Данные — `GET /transfer-check/sessions/{id}/source-stock`, отдельный от detail
 * запрос: detail перезапрашивается на каждый скан и не должен ждать регистр 1С.
 */
import type React from "react";

import type { NeedLine } from "@/api/order_check";
import type { SourceStockRow, TransferSourceStock } from "@/api/transfer_check";
import { REMAINING_EPS, norm } from "@/lib/order-check";
import { cn } from "@/lib/utils";

/** Состояние запроса остатка склада-источника (подмножество `useQuery`). */
export type SourceStockState = {
  data: TransferSourceStock | undefined;
  isPending: boolean;
  error: unknown;
};

/** Мелкая строка остатка склада-источника под позицией.
 *
 * Остаток 1С как есть: отсканированное не вычтено (регистр не уменьшается до
 * проведения). Подсветка — если свободного меньше, чем осталось собрать:
 * янтарь — часть есть, красный — свободного нет вовсе. «В пути на приёмке» —
 * отправленное другими перемещениями и не принятое (`in_transit`): гейт остатка
 * его вычитает, без строки отказ «свободно 4» при видимых 10 непонятен.
 * Ошибку запроса строка не рисует — она одна, в шапке листа. */
export function SourceStockLine({
  n,
  stock,
  rowsByKey,
  sN,
  u,
}: {
  n: NeedLine;
  stock: SourceStockState;
  rowsByKey: Map<string, Pick<SourceStockRow, "qty_free" | "qty_total" | "in_transit" | "qty_reserved">>;
  sN: (base: number) => string;
  u: string;
}): React.ReactElement | null {
  if (stock.error) return null;
  if (stock.isPending || !stock.data)
    return <div className="text-[11px] text-slate-400">остаток…</div>;
  const row = rowsByKey.get(`${n.nomenclature_ref}|${norm(n.characteristic_ref)}`);
  if (!row)
    return (
      <div className="text-[11px] font-semibold text-amber-700">
        остаток: строки нет в ответе 1С
      </div>
    );
  const remaining = Math.max(0, n.qty_needed - n.qty_scanned);
  const short = remaining > REMAINING_EPS && row.qty_free + REMAINING_EPS < remaining;
  // Резерв — с бэка (`qty_reserved`): `qty_free` там уже за вычетом «в пути»
  // и обрезан по 0, разность `всего − свободно − в пути` врала бы.
  const reserved = row.qty_reserved;
  return (
    <div
      className={cn(
        "text-[11px]",
        !short
          ? "text-slate-500"
          : row.qty_free > REMAINING_EPS
            ? "font-semibold text-amber-700"
            : "font-semibold text-red-700",
      )}
    >
      на складе: свободно {sN(row.qty_free)} {u}
      {reserved > REMAINING_EPS &&
        ` · всего ${sN(row.qty_total)} (резерв ${sN(reserved)})`}
      {row.in_transit > REMAINING_EPS &&
        ` · ещё ${sN(row.in_transit)} ${u} в пути на приёмке`}
    </div>
  );
}
